import { useAuthStore } from '@/stores/auth'
import { maSessionStorage } from '@/lib/storage'
import maDate from '@/base/MaDate'
import Device from '@/base/constants/Device'
import { AUTH_PLATFORM } from '@/base/constants/Auth'
import { AnalyticsEvents, AnalyticsProps } from '@/base/constants/Analytics'

export default function analyticsService(segmentAnalytics, router) {
  const authStore = useAuthStore()

  /**
   * Returns the current page or tab information
   */
  const sourcePageAndTab = () => {
    const currentRoute = router.currentRoute?.value
    return {
      [AnalyticsProps.PAGE]: currentRoute?.meta?.analyticsPageName,
    }
  }

  /**
   * Sends a track event to Segment.
   * events: AnalyticsEvents.GENERAL_CLICK, etc.
   */
  const track = (eventName, properties = {}) => {
    segmentAnalytics.track(eventName, {
      ...sourcePageAndTab(),
      ...properties,
    }, {
      context: { groupId: authStore.jwtData?.accountId },
    })
  }

  /**
   * Sends a page view event when navigating between pages
   */
  const trackPageView = (to, from) => {
    if (to.name === from.name)
      return

    const properties = {
      [AnalyticsProps.PRODUCT_FAMILY]: to.meta.productFamily,
      [AnalyticsProps.PAGE]: to.meta.analyticsPageName,
      [AnalyticsProps.PREVIOUS_PAGE]: from.meta.analyticsPageName,
    }

    segmentAnalytics.page(
      AnalyticsEvents.PAGE_VIEW,
      properties,
    )
  }

  /**
   * Checks if the platform is Search Ads
   */
  const isSearchAdsPlatform = () => {
    return maSessionStorage.platform === AUTH_PLATFORM.SA
  }

  /**
   * Sends an onboarding complete event
   */
  const onboardingComplete = async (username, userId, userProduct) => {
    // Identify the user
    await segmentAnalytics.identify(userId, {
      [AnalyticsProps.USER_ID]: userId,
      [AnalyticsProps.EMAIL]: username,
    })

    await segmentAnalytics.track(AnalyticsEvents.ONBOARDING_COMPLETION, {
      [AnalyticsProps.EMAIL]: username,
      [AnalyticsProps.PRODUCT]: userProduct[0]?.name,
    })
  }

  /**
   * Sends a signup event
   */
  const signupTrack = async (userId, loginBy, intercomUserHash) => {
    const username = authStore.jwtData?.username

    // Identify the user
    await segmentAnalytics.identify(userId, {
      [AnalyticsProps.PLATFORM]: 'MOBILEACTION_CO',
      [AnalyticsProps.REGISTRATION_DATE]: maDate().formatISODate(),
      [AnalyticsProps.USER_ID]: userId,
      [AnalyticsProps.EMAIL]: username,
    }, { integrations: {
      Intercom: {
        user_hash: intercomUserHash,
      },
    } })

    if (isSearchAdsPlatform()) {
      await segmentAnalytics.track(AnalyticsEvents.SA_SIGNUP, {
        [AnalyticsProps.SA_SIGNUP_PRODUCT]: 'SA',
        [AnalyticsProps.SA_USER_ID]: userId,
        [AnalyticsProps.SA_SIGNUP_BY]: loginBy,
      })
    }
    else {
      await segmentAnalytics.track(AnalyticsEvents.SIGNUP, {
        [AnalyticsProps.PLATFORM]: 'MOBILEACTION_CO',
        [AnalyticsProps.LOGIN_BY]: loginBy,
        [AnalyticsProps.EMAIL]: username,
      })
    }
  }

  /**
   * Sends a login event
   */
  const loginTrack = (userId, loginBy) => {
    if (isSearchAdsPlatform()) {
      segmentAnalytics.track(AnalyticsEvents.SA_LOGIN, {
        [AnalyticsProps.SA_LOGIN_PRODUCT]: 'SA',
        [AnalyticsProps.SA_USER_ID]: userId,
        [AnalyticsProps.SA_LOGIN_BY]: loginBy,
      })
    }
    else {
      segmentAnalytics.track(AnalyticsEvents.LOGIN, {
        [AnalyticsProps.LOGIN_BY]: loginBy,
      })
    }
  }

  /**
   * Sends a signup event if isNewUser = true, otherwise sends a login event
   */
  const login = (isNewUser, userId, loginBy, intercomUserHash) => {
    if (isNewUser) {
      signupTrack(userId, loginBy, intercomUserHash)
    }
    else {
      loginTrack(userId, loginBy)
    }
  }

  /**
   * Resets the user's anonymous identity in Segment
   */
  const resetUser = () => {
    segmentAnalytics.reset()
  }

  /**
   * Identifies the user in Segment
   */
  const identifyUser = ({ userInfo, impersonation }) => {
    const { username, name, surname, accountId, userId, intercomUserHash, createdAt } = userInfo

    // If impersonation is active, the userId may differ
    const userIdForSegment = impersonation ? `${impersonation}:${userId}` : userId
    const userEmailForSegment = impersonation
      ? `MA-admin:${impersonation}:${username}`
      : username

    const userProps = {
      [AnalyticsProps.ACCOUNT_ID]: accountId,
      [AnalyticsProps.USER_ID]: userIdForSegment,
      [AnalyticsProps.FIRST_LOGIN]: createdAt,
      [AnalyticsProps.LAST_LOGIN]: new Date(authStore.jwtData?.iat * 1000).toISOString(),
      email: userEmailForSegment,
      imp: impersonation,
      name: `${name || ''} ${surname || ''}`,
    }

    const integrationProps = {
      integrations: {
        Intercom: {
          user_hash: intercomUserHash,
        },
      },
    }

    // If initialReferrer exists
    if (maSessionStorage.initialReferrer) {
      userProps.$ma_initial_referrer = maSessionStorage.initialReferrer
    }

    segmentAnalytics.identify(userIdForSegment, userProps, integrationProps)
  }

  // specific track functions:
  const appSelection = ({ trackId, isHeader = false }) => {
    track(AnalyticsEvents.APP_SELECTION, {
      [AnalyticsProps.APP_ID]: trackId,
      [AnalyticsProps.LOCATION]: isHeader ? 'Header' : 'Page',
    })
  }

  const countrySelection = ({ code, isHeader = false }) => {
    const getCode = c => (typeof c === 'string' ? c : c?.code ?? '')

    const countryCode = Array.isArray(code)
      ? code.map(c => getCode(c).toUpperCase())
      : getCode(code).toUpperCase()

    track(AnalyticsEvents.COUNTRY_SELECTION, {
      [AnalyticsProps.COUNTRY_CODE]: countryCode,
      [AnalyticsProps.LOCATION]: isHeader ? 'Header' : 'Page',
    })
  }

  const dateSelection = ({ type, value }) => {
    const getDateValue = () => {
      const formatDate = d => maDate(d).formatISODate()
      return Array.isArray(value) ? value.map(formatDate) : formatDate(value)
    }

    track(AnalyticsEvents.DATE_SELECTION, {
      [AnalyticsProps.TYPE]: type,
      [AnalyticsProps.VALUE]: getDateValue(),
    })
  }

  const categorySelection = (categoryIds) => {
    track(AnalyticsEvents.CATEGORY_SELECTION, {
      [AnalyticsProps.CATEGORY]: Array.isArray(categoryIds) ? categoryIds : [categoryIds],
    })
  }

  const deviceSelection = (device) => {
    let deviceDisplayName = null
    switch (device) {
      case Device.Iphone:
        deviceDisplayName = 'iPhone'
        break
      case Device.Ipad:
        deviceDisplayName = 'iPad'
        break
      case Device.RealityDevice:
        deviceDisplayName = 'Reality Device'
        break
      case Device.Android:
        deviceDisplayName = 'Android'
        break
      default:
        deviceDisplayName = 'All'
        break
    }

    track(AnalyticsEvents.DEVICE_SELECTION, {
      [AnalyticsProps.DEVICE]: deviceDisplayName,
    })
  }

  const downloadCsv = (csvDownloadLocation) => {
    track(AnalyticsEvents.DOWNLOAD_CSV, {
      [AnalyticsProps.CSV_DOWNLOAD_LOCATION]: csvDownloadLocation,
    })
  }

  const keywordTranslation = (numberOfKeywords, countryCode) => {
    track(AnalyticsEvents.KEYWORD_TRANSLATION, {
      [AnalyticsProps.NUMBER_OF_KEYWORDS]: numberOfKeywords,
      [AnalyticsProps.DESTINATION_COUNTRY]: countryCode,
    })
  }

  const asaIntegrationClicked = (place, experiment = '') => {
    track(AnalyticsEvents.ASA_INTEGRATION_CLICKED, {
      Place: place,
      Experiment: experiment,
    })
  }

  const appIntegration = () => {
    track(AnalyticsEvents.APP_INTEGRATION, {})
  }

  const viewMore = (fromRouteName, toRouteName) => {
    const from = router.resolve({ name: fromRouteName })?.meta?.analyticsPageName
    const to = toRouteName
      ? router.resolve({ name: toRouteName })?.meta?.analyticsPageName
      : from

    segmentAnalytics.track(AnalyticsEvents.VIEW_MORE, {
      [AnalyticsProps.PAGE]: from,
      [AnalyticsProps.DESTINATION_PAGE]: to,
    })
  }

  const sideMenuClick = ({ title, productFamily }) => {
    track(AnalyticsEvents.SIDE_MENU_CLICKED, {
      [AnalyticsProps.PRODUCT_FAMILY]: productFamily,
      [AnalyticsProps.MENU]: title,
    })
  }

  const previewScreenshot = (type = 'Screenshot') => {
    track(AnalyticsEvents.SCREENSHOT_PREVIEW_CLICKED, {
      [AnalyticsProps.TYPE]: type,
    })
  }

  // Intercom functions
  const showIntercomMessenger = () => {
    window?.Intercom('show')
  }

  const hideIntercomMessenger = () => {
    window?.Intercom('hide')
  }

  const clearIntercomUserInfo = () => {
    if (window?.Intercom) {
      window?.Intercom('shutdown')
      window?.Intercom('boot')
    }
  }

  return {
    track,
    trackPageView,
    sourcePageAndTab,
    login,
    resetUser,
    onboardingComplete,
    identifyUser,
    appSelection,
    dateSelection,
    countrySelection,
    categorySelection,
    deviceSelection,
    downloadCsv,
    keywordTranslation,
    asaIntegrationClicked,
    appIntegration,
    viewMore,
    sideMenuClick,
    previewScreenshot,
    showIntercomMessenger,
    hideIntercomMessenger,
    clearIntercomUserInfo,
  }
}
